import React from 'react'
import {
  CorePersonalFieldsPartial,
  schemaCorePersonalFields,
} from '../../../../CorePersonalFieldsPartial/CorePersonalFieldsPartial'
import { CorePersonalFields } from 'common/types/CorePersonalFields'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { ModalFooter } from '../../../../../../../common/components/wizard/ModalFooter'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { TextareaAutosize } from '@mui/material'
import { commonShadowStyles, commonTextareaStyles } from '../../../../common/InputStyles'
import { SimpleErrorMessage } from '../../../../common/SimpleErrorMessage'
import { ReportCategory } from 'common/types/ReportProfile'

const schemaStep2ReportDetails = yup.object().shape({
  reportCategory: yup.mixed<ReportCategory>().nullable(),
  message: yup.string().required('Please leave your request or suggestion'),
  agreeToTerms: yup
    .boolean()
    .oneOf([true], 'Please check to signify agreement'),
})

const combinedSchema: yup.ObjectSchema<ReportDetailsFields> =
  schemaCorePersonalFields.concat(schemaStep2ReportDetails)

export type ReportDetailsFields = CorePersonalFields & {
  reportCategory?: ReportCategory | null
  message?: string
  agreeToTerms?: boolean
}

type Step2ReportDetailsProps = {
  reportCategory?: ReportCategory | null
  initialValues?: ReportDetailsFields
  saveValues: (values: ReportDetailsFields) => void
  handleStep2Submit: (values: ReportDetailsFields) => void
}

export const Step2ReportDetails = ({
  initialValues,
  reportCategory,
  handleStep2Submit,
  saveValues,
}: Step2ReportDetailsProps) => {
  const methodsRHF = useForm<ReportDetailsFields>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      emailAddress: initialValues?.emailAddress || '',
      phoneNumber: initialValues?.phoneNumber || '',
      reportCategory: initialValues?.reportCategory ?? reportCategory,
      message: initialValues?.message || '',
      agreeToTerms: initialValues?.agreeToTerms || false,
    },
    criteriaMode: 'all',
    resolver: yupResolver(combinedSchema),
  })

  const doValidation = async () => {
    const isValid = await methodsRHF.trigger()
    if (isValid) {
      methodsRHF.handleSubmit(onSubmit)()
    }
    return isValid
  }

  const onSubmit = (data: ReportDetailsFields) => {
    handleStep2Submit?.(data)
  }

  const handleFieldBlur = () => {
    saveValues(methodsRHF.getValues())
  }

  return (
    <>
      <FormProvider {...methodsRHF}>
        <form>
          <div className='flex flex-col gap-2 py-2'>
            <CorePersonalFieldsPartial className='grid grid-cols-1 md:grid-cols-2 gap-2' />
            <Controller
              name='message'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextareaAutosize
                    id='message'
                    className={`min-h-24 ${commonTextareaStyles} ${field.value
                      ? 'text-[#00323D]'
                      : 'rounded-lg text-[#818285]'}`}
                    style={commonShadowStyles}
                    placeholder='* Message'
                    {...field}
                    onBlur={(e) => {
                      field.onBlur()
                      handleFieldBlur()
                    }}
                  />
                  {error && <SimpleErrorMessage message={error.message} />}
                </>
              )}
            />
            <Controller
              name='agreeToTerms'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <label className='flex items-center mt-4'>
                    <input
                      type='checkbox'
                      onChange={field.onChange}
                      onBlur={(e) => {
                        field.onBlur()
                      }}
                      name={field.name}
                      ref={field.ref}
                      checked={field.value}
                    />
                    <span className='ml-2 text-xs md:text-sm'>
                      I agree with{' '}
                      <a
                        href='/terms-and-policies/terms-of-use-consumer'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-[#37A2BA] hover:underline'
                      >
                        Terms
                      </a>{' '}
                      and{' '}
                      <a
                        href='/terms-and-policies/data-protection-policy'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-[#37A2BA] hover:underline'
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                  {error && <SimpleErrorMessage message={error.message} />}
                </>
              )}
            />
          </div>
        </form>
      </FormProvider>
      <ModalFooter
        showMandatoryFieldMessage
        doBeforeNextStep={doValidation}
        forwardButtonText='Send'
      />
    </>
  )
}
